// src/theme/ThemeProvider.tsx
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useColorScheme } from "react-native";
import { resolveTheme, Theme, THEME_PRESETS } from "./themes";

const THEME_MODE_KEY = "ferina_theme_mode";
const THEME_PRESET_KEY = "ferina_theme_preset";

type ThemeMode = "light" | "dark";

type ThemeContextValue = {
  theme: Theme;
  mode: ThemeMode;
  presetId: string;
  setMode: (m: ThemeMode) => void;
  setPreset: (id: string) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: resolveTheme("care", "light"),
  mode: "light",
  presetId: "care",
  setMode: () => {},
  setPreset: () => {},
  toggle: () => {},
});

function getSystemMode(colorScheme: "light" | "dark" | null): ThemeMode {
  return colorScheme === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const system = useColorScheme();
  const systemMode = getSystemMode(system);

  const [mode, setModeState] = useState<ThemeMode>(systemMode);
  const [presetId, setPresetIdState] = useState("care");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      AsyncStorage.getItem(THEME_MODE_KEY),
      AsyncStorage.getItem(THEME_PRESET_KEY),
    ])
      .then(([storedMode, storedPreset]) => {
        if (!mounted) return;
        if (storedMode === "light" || storedMode === "dark") {
          setModeState(storedMode);
        }
        if (storedPreset && THEME_PRESETS.some((p) => p.id === storedPreset)) {
          setPresetIdState(storedPreset);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    AsyncStorage.setItem(THEME_MODE_KEY, m).catch(() => {});
  }, []);

  const setPreset = useCallback((id: string) => {
    setPresetIdState(id);
    AsyncStorage.setItem(THEME_PRESET_KEY, id).catch(() => {});
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      AsyncStorage.setItem(THEME_MODE_KEY, next).catch(() => {});
      return next;
    });
  }, []);

  const theme = useMemo(
    () => resolveTheme(presetId, mode),
    [presetId, mode],
  );

  const value = useMemo(
    () => ({
      theme,
      mode,
      presetId,
      setMode,
      setPreset,
      toggle,
    }),
    [theme, mode, presetId, setMode, setPreset, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
